import { config } from '../config/app.config';

export interface Product {
  ID: string;
  TITLE: string;
  DESCRIPTION: string;
  IMAGE: string;
  LINK: string;
  CATEGORY: string;
  ACTIVE: boolean;
  AVAILABILITY: string;
}

export interface SupportFAQ {
  CATEGORY: string;
  TITLE: string;
  ANSWER: string;
}

const SPREADSHEET_ID = config.googleSheets.spreadsheetId;

export class GoogleSheetsService {
  private static instance: GoogleSheetsService;
  
  static getInstance(): GoogleSheetsService {
    if (!GoogleSheetsService.instance) {
      GoogleSheetsService.instance = new GoogleSheetsService();
    }
    return GoogleSheetsService.instance;
  }

  async getSupportFAQ(): Promise<SupportFAQ[]> {
    try {
      // Usando CSV export da aba suport do Google Sheets
      const csvUrl = `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/export?format=csv&gid=177784412`;
      
      try {
        const response = await fetch(csvUrl);
        
        if (response.ok) {
          const csvText = await response.text();
          
          // Verificar se os dados parecem ser de suporte (procurar por cabeçalhos esperados)
          const firstLine = csvText.split('\n')[0]?.toLowerCase();
          if (firstLine && (firstLine.includes('category') || firstLine.includes('titulo') || firstLine.includes('pergunta'))) {
            const supportData = this.parseCSVToSupport(csvText);
            if (supportData.length > 0) {
              return supportData;
            }
          }
        }
      } catch (err) {
        console.warn(`Erro ao tentar URL ${csvUrl}:`, err);
      }
      
      console.warn('Nenhuma aba de suporte encontrada, retornando array vazio');
      return [];
    } catch (error) {
      console.error('Erro ao carregar dados de suporte do Google Sheets:', error);
      return [];
    }
  }

  private parseCSVToSupport(csvText: string): SupportFAQ[] {
    const lines = csvText.split('\n');
    if (lines.length < 2) return [];

    // Remove header line
    const dataLines = lines.slice(1);
    
    return dataLines
      .filter(line => line.trim()) // Remove empty lines
      .map(line => this.parseCSVLineToSupport(line))
      .filter((item): item is SupportFAQ => item !== null);
  }

  private parseCSVLineToSupport(line: string): SupportFAQ | null {
    try {
      // Simple CSV parser - handles basic cases
      const values = this.parseCSVRow(line);
      
      if (values.length < 3) return null;

      const [category, title, answer] = values;

      return {
        CATEGORY: category?.trim() || '',
        TITLE: title?.trim() || '',
        ANSWER: answer?.trim() || ''
      };
    } catch (error) {
      console.error('Erro ao processar linha CSV de suporte:', line, error);
      return null;
    }
  }

  async getProducts(): Promise<Product[]> {
    try {
      // Usando CSV export do Google Sheets para acesso público
      const csvUrl = `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/export?format=csv&gid=0`;
      
      const response = await fetch(csvUrl);
      
      if (!response.ok) {
        console.warn('Planilha não acessível, usando dados de exemplo');
        return [];
      }
      
      const csvText = await response.text();
      
      const products = this.parseCSVToProducts(csvText);
      
      // Se não conseguiu parsear nenhum produto, usa dados de exemplo
      if (products.length === 0) {
        console.warn('Nenhum produto encontrado na planilha, usando dados de exemplo');
        return [];
      }
      
      return products;
    } catch (error) {
      console.error('Erro ao carregar dados do Google Sheets:', error);
      console.warn('Usando dados de exemplo como fallback');
      return [];
    }
  }

  private parseCSVToProducts(csvText: string): Product[] {
    const lines = csvText.split('\n');
    
    if (lines.length < 2) return [];

    // Remove header line
    const dataLines = lines.slice(1);
    const nonEmptyLines = dataLines.filter(line => line.trim());
    
    const products = nonEmptyLines
      .map((line, index) => {
        const product = this.parseCSVLine(line);
        if (!product) {
          console.warn(`Linha ${index + 2} não pôde ser parseada:`, line.substring(0, 100));
        }
        return product;
      })
      .filter((product): product is Product => product !== null);
    
    return products;
  }

  private parseCSVLine(line: string): Product | null {
    try {
      // Simple CSV parser - handles basic cases
      const values = this.parseCSVRow(line);
      
      if (values.length < 8) {
        console.warn(`Linha tem apenas ${values.length} campos (esperado: 8):`, values);
        return null;
      }

      const [id, title, description, image, link, category, active, availability] = values;

      return {
        ID: id?.trim() || '',
        TITLE: title?.trim() || '',
        DESCRIPTION: description?.trim() || '',
        IMAGE: image?.trim() || '',
        LINK: link?.trim() || '',
        CATEGORY: category?.trim() || '',
        ACTIVE: this.parseBoolean(active?.trim()),
        AVAILABILITY: availability?.trim() || ''
      };
    } catch (error) {
      console.error('Erro ao processar linha CSV:', line, error);
      return null;
    }
  }

  private parseCSVRow(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"' && inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else if (char === '"') {
        // Toggle quote state
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current);
    
    return result;
  }

  private parseBoolean(value: string): boolean {
    if (!value) return false;
    const normalized = value.toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'sim' || normalized === 'yes';
  }
}

export const googleSheetsService = GoogleSheetsService.getInstance();